import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import AuroraBackground from '../components/AuroraBackground';
import StatusPill from '../components/StatusPill';
import { MODULES, MODULE_ICONS } from '../data/modules';
import { api } from '../api/client';

export default function Wishlist() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [interested, setInterested] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const toggleModule = (slug: string) => {
    setInterested((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.post('/wishlist', {
        email,
        fullName: fullName || undefined,
        phone: phone || undefined,
        interestedModules: interested,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <SiteNav />

      <div className="relative flex-1">
        <AuroraBackground variant="subtle" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display text-4xl font-bold text-textPrimary mb-3">Join the Waitlist</h1>
            <p className="text-textSecondary mb-10">
              Bankably's mobile app is where you sign up and bank — it isn't publicly released yet. Tell us
              you're interested and which modules you care about most, and we'll let you know the moment it's
              available.
            </p>
          </motion.div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-successSurface border border-success/30 rounded-2xl p-8 text-center"
            >
              <CheckCircle2 className="mx-auto text-success mb-4" size={48} />
              <h2 className="font-display text-2xl font-bold text-textPrimary mb-2">You're on the list</h2>
              <p className="text-textSecondary">We'll email {email} as soon as the mobile app is publicly available.</p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              onSubmit={handleSubmit}
              className="bg-surface1 rounded-2xl p-8 border border-border"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-textPrimary font-semibold mb-2">Full Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg bg-surface2 border border-border text-textPrimary focus:border-gold focus:outline-none"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="block text-textPrimary font-semibold mb-2">Phone (optional)</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg bg-surface2 border border-border text-textPrimary focus:border-gold focus:outline-none"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-textPrimary font-semibold mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 rounded-lg bg-surface2 border border-border text-textPrimary focus:border-gold focus:outline-none"
                  placeholder="you@example.com"
                />
              </div>

              <div className="mb-6">
                <label className="block text-textPrimary font-semibold mb-3">
                  Which modules are you most interested in?
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {MODULES.map((m) => {
                    const Icon = MODULE_ICONS[m.icon] ?? MODULE_ICONS.Wallet;
                    const checked = interested.includes(m.slug);
                    return (
                      <label
                        key={m.slug}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer text-sm transition ${
                          checked
                            ? 'border-gold/50 bg-gold/10 text-textPrimary'
                            : 'border-border text-textSecondary hover:border-gold/20 hover:bg-surface2'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleModule(m.slug)}
                          className="accent-gold"
                        />
                        <Icon size={15} className={checked ? 'text-gold' : 'text-textTertiary'} />
                        <span className="flex-1">{m.shortName}</span>
                        <StatusPill status={m.status} />
                      </label>
                    );
                  })}
                </div>
              </div>

              {error && <p className="text-error text-sm mb-4">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-gold-gradient text-bg font-bold rounded-lg hover:opacity-90 transition disabled:opacity-50"
              >
                {submitting ? 'Joining...' : 'Join the Waitlist'}
              </button>
            </motion.form>
          )}
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
